/**
 * The ONE control that sets a beneficiary's AOTC reserve for a chosen tax year.
 * The beneficiary card and the Add/Edit beneficiary dialog both use it. (ZEN-23)
 */
import React, { useState, useId, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { taxYears, money } from "@/lib/format";
import { AotcYesPanel, aotcCredit } from "@/components/AotcShared";
import { toast } from "sonner";

const thisYear = () => new Date().getFullYear();

/** A year named in a link is honoured only if the chooser offers it. */
export function offeredTaxYear(value) {
  const n = Number(value);
  return taxYears().includes(n) ? n : null;
}

/**
 * The reserve for one beneficiary and ONE tax year: what is stored, what has
 * been changed on screen, and the only save. `year` is the single source for
 * what is read, what is shown and what is saved.
 */
export function useAotcReserve({ studentId, active = true, startYear = null }) {
  const qc = useQueryClient();
  const firstYear = () => offeredTaxYear(startYear) || thisYear();
  const [year, setYearState] = useState(firstYear);
  // The unsaved change, if any. It is worked out against what is stored, never
  // copied from it, so there is no moment when one year's answer sits under another.
  const [draft, setDraft] = useState(null);
  // True from the moment a save is sent until the saved year has been read
  // back. Nothing can be changed in between, so nothing typed is ever dropped.
  const [saving, setSaving] = useState(false);
  // A dialog opened again, or another beneficiary, starts clean on the calendar
  // year. Done while rendering so the old state is never shown.
  const session = `${studentId || ""}|${active}`;
  const [seen, setSeen] = useState(session);
  // Each time a year comes on screen it is asked for afresh, under its own
  // entry: an answer remembered from an earlier look is never shown as stored.
  const instance = useId();
  const [visit, setVisit] = useState(0);
  if (seen !== session) { setSeen(session); setDraft(null); setYearState(firstYear()); setVisit((n) => n + 1); }

  const asking = active && !!studentId;
  const queryKey = ["aotc-reserve", studentId, year, instance, visit];
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey,
    queryFn: async () => (await api.get(`/dashboard/aotc-reserve/${studentId}?tax_year=${year}`)).data,
    enabled: asking,
  });
  const loading = asking && isLoading;
  // A stored answer is shown ONLY if the most recent read succeeded. If the
  // latest read failed, an answer read earlier is not shown in its place:
  // that is not "Not sure" either. No answer is shown as chosen, and only an
  // answer she picks can be saved.
  const unknown = asking && !isLoading && (isError || (!isFetching && data === undefined));
  const origElig = data?.aotc_eligibility || "not_sure";
  const origReserve = data?.target || 0;
  const cap = data?.max_per_year || 4000;

  const elig = draft ? draft.elig : (unknown ? null : origElig);
  const manual = draft ? draft.manual : (origReserve > 0 ? String(origReserve) : "");
  const reserve = manual === "" ? 0 : (parseFloat(manual) || 0);
  const isDirty = !loading && !!draft && !!elig && (unknown || elig !== origElig || reserve !== origReserve);
  const edit = (change) => { if (!loading && !saving) setDraft({ elig, manual, ...change }); };
  const setElig = (v) => edit({ elig: v, manual: v !== "yes" ? "" : (manual || String(cap)) });
  const setManual = (m) => edit({ manual: m });
  const reset = () => { if (!saving) setDraft(null); };
  // An unsaved change belongs to the year it was typed under; it never moves.
  const setYear = (y) => { if (!isDirty && !loading && !saving && offeredTaxYear(y)) { setDraft(null); setYearState(Number(y)); setVisit((n) => n + 1); } };
  const save = async (idToSave = studentId) => {
    if (saving || !elig) return;
    setSaving(true);
    try {
      await api.post("/tax-year-inputs", { student_id: idToSave, tax_year: year, aotc_eligibility: elig, aotc_llc_reserved: elig === "yes" ? reserve : 0 });
      qc.invalidateQueries({ queryKey: ["dash-student"] });
      qc.invalidateQueries({ queryKey: ["tax-year-inputs"] });
      qc.invalidateQueries({ queryKey: ["dashboard-tax-year"] });
      // Read the saved year back before letting go of what was typed. What was
      // typed is dropped ONLY when that read itself succeeded; if it fails, it
      // stays on screen rather than the old answer. Only this control's own
      // year is waited for; the rest refresh on their own.
      const mine = JSON.stringify(queryKey);
      qc.invalidateQueries({ queryKey: ["aotc-reserve"], predicate: (q) => JSON.stringify(q.queryKey) !== mine });
      let readBack = true;
      if (asking && idToSave === studentId) {
        try { await qc.refetchQueries({ queryKey, exact: true }, { throwOnError: true }); }
        catch { readBack = false; }
      }
      if (readBack) setDraft(null);
    } finally { setSaving(false); }
  };
  return { year, setYear, elig, setElig, manual, setManual, reserve, cap, capUnconfirmed: data?.cap_unconfirmed || false, loading, saving, locked: saving, isDirty, reset, save };
}

/** Heading with the year chooser, the question, the three answers and the reserve box. */
export function AotcReserveFields({ form, studentId, dialogName }) {
  const reserveRef = useRef(null);
  const dialog = dialogName !== undefined;
  const who = dialog ? (dialogName || "this beneficiary") : "this beneficiary";
  const { year, elig } = form;
  return (
    <>
      <div className="flex items-center justify-between">
        <label className={`flex items-center gap-1.5 font-bold uppercase tracking-wide ${dialog ? "text-sm" : "text-[11px] text-[#034AA2]"}`}>
          American Opportunity Tax Credit (AOTC) &middot; Tax year
          <select value={year} onChange={(e) => form.setYear(e.target.value)} disabled={form.isDirty || form.loading || form.saving}
            data-testid={dialog ? "dialog-aotc-year" : `aotc-card-year-${studentId}`}
            className="h-7 rounded-md border border-[#C2C9D1] bg-white px-1.5 text-xs font-bold text-[#0A0A0A] disabled:opacity-60">
            {taxYears().map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
      </div>
      {form.loading && <div className="mt-2 h-10 animate-pulse rounded-lg bg-[#E9E9EB]" />}
      {!form.loading && <>
      {dialog
        ? <p className="mt-1 text-xs text-[#6b7280]">Do you expect to claim the AOTC for {who} for tax year {year}?</p>
        : <p className="mt-1 text-[11px] text-[#6b7280]">Claiming the AOTC for this beneficiary for tax year {year}?</p>}
      <div className={dialog ? "mt-2 flex flex-wrap gap-2" : "mt-1.5 flex flex-wrap gap-1.5"}>
        {[["yes", "Yes"], ["no", "No"], ["not_sure", "Not sure"]].map(([v, lbl]) => (
          <label key={v} className={`flex items-center rounded-md border font-semibold cursor-pointer transition-colors ${dialog ? "gap-2 px-3 py-2 text-sm" : "gap-1.5 px-2.5 py-1.5 text-xs"} ${elig === v ? "border-[#034AA2] bg-[#034AA2]/10 text-[#034AA2]" : "border-[#C2C9D1] bg-white text-[#374151] hover:bg-[#E9E9EB]"}`}>
            <input type="radio" name={dialog ? "aotc-elig-dialog" : `aotc-card-${studentId}`} checked={elig === v} disabled={form.locked} onChange={() => form.setElig(v)} className="sr-only" data-testid={dialog ? `dialog-aotc-elig-${v}` : `aotc-card-elig-${studentId}-${v}`} />{lbl}
          </label>
        ))}
      </div>
      {elig === "yes" && <AotcYesPanel aotcManual={form.manual} setAotcManual={form.setManual} studentName={who} taxYear={year} aotcCap={form.cap} capUnconfirmed={form.capUnconfirmed} reserveRef={reserveRef} />}
      </>}
    </>
  );
}

/** The beneficiary card's control: the shared fields plus its own Save and Cancel. */
export function AotcCardControl({ studentId, startYear = null }) {
  const form = useAotcReserve({ studentId, startYear });
  const { year, elig, saving } = form;

  const handleSave = async () => {
    try {
      await form.save();
      toast.success(`AOTC setting saved for tax year ${year}`);
    } catch { toast.error("Failed to save AOTC setting"); }
  };

  const effectAmt = elig === "yes" ? form.reserve : 0;
  return (
    <div className="mt-3 rounded-lg border border-[#C2C9D1] bg-[#F4F7FB] p-3" data-testid={`aotc-card-control-${studentId}`}>
      <AotcReserveFields form={form} studentId={studentId} />
      {elig === "yes" && effectAmt > 0 && !form.loading && (
        <p className="mt-2 text-xs text-[#374151]" data-testid={`aotc-effect-${studentId}`}>Reduces {year} AQEE by {money(effectAmt, "USD")} &middot; estimated credit {money(aotcCredit(effectAmt), "USD")}</p>
      )}
      {elig === "not_sure" && !form.loading && (
        <p className="mt-2 text-[11px] text-[#034AA2]"><b>Not sure?</b> The AOTC is worth up to <b>$2,500</b>/year per student. <Link to="/help?article=aotc" className="font-semibold underline">Learn more in the Help Center</Link></p>
      )}
      {form.isDirty && (
        <div className="mt-2 flex justify-end gap-2">
          <button onClick={form.reset} disabled={saving} className="rounded-md border border-[#C2C9D1] bg-white px-3 py-1.5 text-xs font-bold text-[#374151] hover:bg-[#E9E9EB] disabled:opacity-50" data-testid={`aotc-card-cancel-${studentId}`}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="rounded-md bg-[#077A39] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#066030] disabled:opacity-50" data-testid={`aotc-card-save-${studentId}`}>{saving ? "Saving…" : `Save for tax year ${year}`}</button>
        </div>
      )}
    </div>
  );
}
